import React from 'react';
import { Tooltip } from '@mui/material';
import { TaskFullSla, SlaCurrentStatusReadOnly, SlaType } from '@askporter/client-grieg-lyric';
import { formatSlaDeadlineTooltip, slaIcons } from '@askporter/utils';
import { Chip } from '../../Chip';
import Icon, { IconSize } from '../../Icon';
import {
  getDueStatusSlaDisplay,
  getResponseStatusSlaDisplay,
  getResolveStatusSlaDisplay,
  getSlaStatus,
  SlaFilter,
} from '../utils';

export interface TaskCardSlaDisplayProps {
  /**
   * translation method
   */
  t: (key: string) => string;
  /**
   * sla object
   */
  sla: TaskFullSla | undefined;
  /**
   * filter gets passed when filter task search
   */
  slaFilter?: SlaFilter;
}

const slaChipColour = {
  [SlaCurrentStatusReadOnly.Running]: 'primary',
  [SlaCurrentStatusReadOnly.Paused]: 'warning',
  [SlaCurrentStatusReadOnly.CompletedOnTime]: 'success',
  [SlaCurrentStatusReadOnly.CompletedLate]: 'error',
  [SlaCurrentStatusReadOnly.Overdue]: 'error',
  [SlaCurrentStatusReadOnly.Na]: undefined as any,
};

/**
 * TaskCardSla to show SLA data within TaskCard
 *
 */
export const TaskCardSlaDisplay: React.FC<React.PropsWithChildren<TaskCardSlaDisplayProps>> = ({
  sla,
  t,
  slaFilter,
}: TaskCardSlaDisplayProps): JSX.Element | null => {
  if (!sla || sla?.type === SlaType.None) return null;

  const statusObject = getSlaStatus({ sla, slaFilter });
  if (!statusObject) return null;
  const { slaStatusType = null, currentStatus = null } = statusObject;

  let slaDisplayText = null;
  let tooltipText = null;

  if (slaStatusType === 'DueStatus' && sla.due) {
    slaDisplayText = getDueStatusSlaDisplay({
      slaDue: sla.due,
      t,
    });
  } else if (slaStatusType === 'RespondStatus' && sla.response) {
    slaDisplayText = getResponseStatusSlaDisplay({
      slaResponse: sla.response,
      t,
    });
    tooltipText =
      sla.response?.status === 'RUNNING' || sla.response?.status === 'OVERDUE'
        ? formatSlaDeadlineTooltip('task_list', sla.response?.dueDateTime, 'response', t)
        : null;
  } else if (slaStatusType === 'ResolveStatus' && sla.resolution) {
    slaDisplayText = getResolveStatusSlaDisplay({
      slaResolve: sla.resolution,
      t,
    });
    tooltipText =
      sla.resolution?.status === 'RUNNING' || sla.resolution?.status === 'OVERDUE'
        ? formatSlaDeadlineTooltip('task_list', sla.resolution?.dueDateTime, 'resolve', t)
        : null;
  }

  return (
    <>
      {slaDisplayText ? (
        <Tooltip title={tooltipText} placement="top" arrow>
          <Chip
            data-testid="sla-chip"
            color={slaChipColour[currentStatus || 'NA']}
            size="small"
            label={slaDisplayText}
            startAdornment={
              currentStatus
                ? {
                    mode: 'icon',
                    icon: <Icon size={IconSize.SM} folder={slaIcons[currentStatus]} fillColor="inherit" />,
                  }
                : undefined
            }
          />
        </Tooltip>
      ) : null}
    </>
  );
};
